import {
  AlertCircle,
  Building2,
  CalendarClock,
  ChevronRight,
  History,
  ListTree,
  MoreHorizontal,
  Pencil,
  RefreshCw,
  Route,
  Undo2,
} from "lucide-react";
import { useMemo } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaIconButton } from "@/shared/components/buttons/ColocaIconButton";
import { ColocaAmountDisplay } from "@/shared/components/data-display/ColocaAmountDisplay";
import {
  ColocaDataTable,
  type ColocaDataTableColumn,
} from "@/shared/components/data-display/ColocaDataTable";
import { ColocaStatusPill } from "@/shared/components/data-display/ColocaStatusPill";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import { ColocaDropdownMenu, type ColocaDropdownItem } from "@/shared/components/overlays/ColocaDropdownMenu";
import { ColocaPopover } from "@/shared/components/overlays/ColocaPopover";
import { ColocaTooltip } from "@/shared/components/overlays/ColocaTooltip";
import useTranslations from "@/shared/lang/useTranslations";
import { compareDecimalStrings } from "@/shared/utils/decimal";
import { formatTaxLabel } from "@/shared/utils/paymentTaxes";

import { useFeeLabels } from "../../hooks/useFeeLabels";
import type { PaymentTax } from "../../types/paymentTaxes.types";
import { corridorLabel, shortId } from "../../utils/feeFormat";
import type { TaxCorridorCoverage, TaxCorridorLine } from "../../utils/taxCoverage";
import { CorridorIdentity } from "../fees/CorridorIdentity";

export interface TaxCorridorActions {
  onEdit: (coverage: TaxCorridorCoverage) => void;
  onBackToGeneral: (coverage: TaxCorridorCoverage) => void;
  onHistory: (coverage: TaxCorridorCoverage) => void;
  onViewRules: () => void;
  onViewOrganization: (organizationId: string) => void;
}

export interface TaxCorridorsSectionProps extends TaxCorridorActions {
  corridors: TaxCorridorCoverage[];
  /** Corridors before the KPI filter — tells an empty filter apart from an empty catalog. */
  totalCount: number;
  isLoading: boolean;
  routesError: boolean;
  onRetryRoutes: () => void;
  /** `null` is the general taxes. */
  scopeOrganizationId: string | null;
  organizationNames: Record<string, string>;
  canWrite: boolean;
}

const isZero = (value: string) => compareDecimalStrings(value, "0") === 0;

/** One row per corridor, read through the page scope, exactly as the price book reads it. */
export function TaxCorridorsSection({
  corridors,
  totalCount,
  isLoading,
  routesError,
  onRetryRoutes,
  scopeOrganizationId,
  organizationNames,
  canWrite,
  ...actions
}: TaxCorridorsSectionProps) {
  const { t } = useTranslations("paymentTaxes");
  const scoped = scopeOrganizationId != null;

  const columns = useMemo<ColocaDataTableColumn<TaxCorridorCoverage>[]>(
    () => [
      {
        id: "corridor",
        header: t("corridors_th_corridor"),
        cell: (coverage) => <CorridorIdentity route={coverage.corridor.route} layout="stacked" />,
      },
      {
        id: "taxes",
        header: t(scoped ? "corridors_th_taxes_org" : "corridors_th_taxes_general"),
        cell: (coverage) => <TaxesCell coverage={coverage} scoped={scoped} />,
      },
      ...(scoped
        ? []
        : [
            {
              id: "own",
              header: t("corridors_th_own"),
              cell: (coverage: TaxCorridorCoverage) => (
                <OwnRatesCell
                  coverage={coverage}
                  organizationNames={organizationNames}
                  onViewOrganization={actions.onViewOrganization}
                />
              ),
            } satisfies ColocaDataTableColumn<TaxCorridorCoverage>,
          ]),
      {
        id: "client",
        header: t("corridors_th_client"),
        headerClassName: "text-right",
        cellClassName: "text-right",
        cell: (coverage) => <TotalCell value={coverage.clientTotal} coverage={coverage} />,
      },
      {
        id: "coloca",
        header: t("corridors_th_coloca"),
        headerClassName: "text-right",
        cellClassName: "text-right",
        cell: (coverage) => <TotalCell value={coverage.colocaTotal} coverage={coverage} />,
      },
      {
        id: "actions",
        header: "",
        headerClassName: "text-right",
        cellClassName: "text-right",
        cell: (coverage) => (
          <RowActions coverage={coverage} scoped={scoped} canWrite={canWrite} actions={actions} />
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      t,
      scoped,
      canWrite,
      organizationNames,
      actions.onEdit,
      actions.onBackToGeneral,
      actions.onHistory,
      actions.onViewRules,
      actions.onViewOrganization,
    ],
  );

  if (routesError) {
    return (
      <ColocaAlert
        variant="destructive"
        icon={AlertCircle}
        heading={t("routes_error")}
        text={t("routes_error_detail")}
        actions={
          <ColocaButton variant="default" size="sm" icon={RefreshCw} onClick={onRetryRoutes}>
            {t("error_retry")}
          </ColocaButton>
        }
      />
    );
  }

  const empty = (
    <ColocaEmpty
      icon={Route}
      title={t(totalCount > 0 ? "corridors_filtered_empty_title" : "corridors_empty_title")}
      description={t(totalCount > 0 ? "corridors_filtered_empty_description" : "corridors_empty_description")}
      surface="card"
    />
  );

  return (
    <div className="space-y-4">
      <div className="hidden lg:block">
        <ColocaDataTable
          columns={columns}
          rows={corridors}
          rowKey={(coverage) => coverage.corridor.route.id}
          loading={isLoading}
          skeletonRows={6}
          minWidth={scoped ? 980 : 1120}
          empty={empty}
        />
      </div>
      <div className="lg:hidden">
        {isLoading ? (
          <div className="flex flex-col gap-3">
            {[0, 1, 2].map((index) => (
              <div key={index} className="h-40 animate-pulse rounded-xl border border-border bg-muted" />
            ))}
          </div>
        ) : corridors.length === 0 ? (
          empty
        ) : (
          <div className="flex flex-col gap-3">
            {corridors.map((coverage) => (
              <CorridorCard
                key={coverage.corridor.route.id}
                coverage={coverage}
                scoped={scoped}
                canWrite={canWrite}
                organizationNames={organizationNames}
                actions={actions}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function rateLabel(tax: PaymentTax, locale: string, exemptLabel: string): string {
  return compareDecimalStrings(tax.rate, "0") === 0
    ? `${tax.name} · ${exemptLabel}`
    : formatTaxLabel(tax, locale);
}

function TaxLine({ line, scoped, currency }: { line: TaxCorridorLine; scoped: boolean; currency: string }) {
  const { t, activeLanguage } = useTranslations("paymentTaxes");
  const isOwn = line.source === "OWN";
  return (
    <li className="flex flex-col gap-0.5">
      <span className="flex flex-wrap items-baseline justify-between gap-x-3">
        <span className={line.exempt ? "text-sm text-muted-foreground" : "text-sm font-medium"}>
          {rateLabel(line.tax, activeLanguage, t("exempt"))}
        </span>
        {line.amount != null && (
          <ColocaAmountDisplay
            value={line.amount}
            currency={currency}
            size="sm"
            minimumFractionDigits={0}
            maximumFractionDigits={8}
          />
        )}
      </span>
      <span className="text-[11px] text-muted-foreground">
        {t(line.tax.base === "COMMISSION" ? "base_commission" : "base_gross")} ·{" "}
        {t(line.tax.bearer === "CLIENT" ? "bearer_client" : "bearer_coloca")}
        {line.amount == null && !line.exempt && ` · ${t("commission_unknown")}`}
      </span>
      {scoped && (
        <span className="flex flex-wrap items-center gap-1.5">
          <ColocaStatusPill
            variant={isOwn ? "info" : "neutral"}
            size="sm"
            icon={isOwn ? Building2 : undefined}
          >
            {t(isOwn ? "source_own" : "source_general")}
          </ColocaStatusPill>
          {isOwn && (
            <span className="text-[11px] text-muted-foreground">
              {line.general
                ? t("source_general_baseline", {
                    rate: rateLabel(line.general, activeLanguage, t("exempt")),
                  })
                : t("source_no_general")}
            </span>
          )}
        </span>
      )}
    </li>
  );
}

function TaxesCell({ coverage, scoped }: { coverage: TaxCorridorCoverage; scoped: boolean }) {
  const { t } = useTranslations("paymentTaxes");
  return (
    <span className="flex min-w-56 flex-col gap-2">
      {coverage.lines.length === 0 ? (
        <span className="text-xs text-muted-foreground">{t("taxes_none")}</span>
      ) : (
        <ul className="flex flex-col gap-2">
          {coverage.lines.map((line) => (
            <TaxLine
              key={line.tax.id}
              line={line}
              scoped={scoped}
              currency={coverage.corridor.route.currency}
            />
          ))}
        </ul>
      )}
      {coverage.hasScheduledChange && (
        <span className="inline-flex items-center gap-1 text-[11px] font-medium text-blue-700 dark:text-blue-300">
          <CalendarClock className="size-3" aria-hidden="true" />
          {t("scheduled")}
        </span>
      )}
    </span>
  );
}

function OwnRatesCell({
  coverage,
  organizationNames,
  onViewOrganization,
}: {
  coverage: TaxCorridorCoverage;
  organizationNames: Record<string, string>;
  onViewOrganization: (organizationId: string) => void;
}) {
  const { t, activeLanguage } = useTranslations("paymentTaxes");
  if (coverage.overrides.length === 0) {
    return <span className="text-xs text-muted-foreground">{t("own_none")}</span>;
  }
  return (
    <ColocaPopover
      align="start"
      contentClassName="w-96"
      title={t("own_popover_title")}
      description={t("own_popover_hint")}
      trigger={
        <ColocaButton variant="secondary" size="xs" icon={Building2}>
          {t("own_count", { count: coverage.overrides.length })}
        </ColocaButton>
      }
    >
      <div className="flex flex-col gap-1">
        {coverage.overrides.map((override) => (
          <ColocaButton
            key={override.organizationId}
            variant="ghost"
            size="sm"
            className="h-auto w-full justify-between gap-3 py-1.5"
            onClick={() => onViewOrganization(override.organizationId)}
          >
            <span className="flex min-w-0 flex-col items-start">
              <span className="max-w-full truncate">
                {organizationNames[override.organizationId] ?? shortId(override.organizationId)}
              </span>
              <span className="font-mono text-[11px] text-muted-foreground">
                {override.lines.map((tax) => rateLabel(tax, activeLanguage, t("exempt"))).join(" · ")}
              </span>
            </span>
            <ChevronRight className="size-3.5 shrink-0 text-muted-foreground" />
          </ColocaButton>
        ))}
      </div>
    </ColocaPopover>
  );
}

function TotalCell({ value, coverage }: { value: string; coverage: TaxCorridorCoverage }) {
  if (isZero(value)) return <span className="text-xs text-muted-foreground">—</span>;
  return (
    <ColocaAmountDisplay
      value={value}
      currency={coverage.corridor.route.currency}
      size="sm"
      minimumFractionDigits={0}
      maximumFractionDigits={8}
    />
  );
}

function primaryLabelKey(coverage: TaxCorridorCoverage, scoped: boolean) {
  if (!scoped) return "action_edit_taxes" as const;
  return coverage.hasOwn ? ("action_change_special" as const) : ("action_special" as const);
}

function useRowMenu(coverage: TaxCorridorCoverage, scoped: boolean, actions: TaxCorridorActions) {
  const { t } = useTranslations("paymentTaxes");
  const items: ColocaDropdownItem[] = [];
  if (scoped && coverage.hasOwn) {
    items.push({
      label: t("action_back_general"),
      icon: Undo2,
      onClick: () => actions.onBackToGeneral(coverage),
    });
  }
  items.push({ label: t("action_view_rules"), icon: ListTree, onClick: actions.onViewRules });
  return items;
}

function RowActions({
  coverage,
  scoped,
  canWrite,
  actions,
}: {
  coverage: TaxCorridorCoverage;
  scoped: boolean;
  canWrite: boolean;
  actions: TaxCorridorActions;
}) {
  const { t } = useTranslations("paymentTaxes");
  const labels = useFeeLabels();
  const menu = useRowMenu(coverage, scoped && canWrite, actions);
  return (
    <span className="inline-flex items-center justify-end gap-1">
      {canWrite && (
        <ColocaButton variant="secondary" size="xs" icon={Pencil} onClick={() => actions.onEdit(coverage)}>
          {t(primaryLabelKey(coverage, scoped))}
        </ColocaButton>
      )}
      <ColocaTooltip
        triggerRender={
          <ColocaIconButton
            icon={History}
            variant="ghost"
            size="sm"
            aria-label={t("action_history")}
            onClick={() => actions.onHistory(coverage)}
          />
        }
      >
        {t("action_history")}
      </ColocaTooltip>
      <ColocaDropdownMenu
        align="end"
        items={menu}
        trigger={
          <ColocaIconButton
            icon={MoreHorizontal}
            variant="ghost"
            size="sm"
            aria-label={t("action_more_aria", {
              corridor: corridorLabel(coverage.corridor.route, labels.directionShort),
            })}
          />
        }
      />
    </span>
  );
}

function CorridorCard({
  coverage,
  scoped,
  canWrite,
  organizationNames,
  actions,
}: {
  coverage: TaxCorridorCoverage;
  scoped: boolean;
  canWrite: boolean;
  organizationNames: Record<string, string>;
  actions: TaxCorridorActions;
}) {
  const { t } = useTranslations("paymentTaxes");
  const labels = useFeeLabels();
  const menu = useRowMenu(coverage, scoped && canWrite, actions);

  return (
    <article className="space-y-3 rounded-xl border border-border bg-card p-4">
      <CorridorIdentity route={coverage.corridor.route} layout="stacked" />
      <TaxesCell coverage={coverage} scoped={scoped} />

      <dl className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-2 text-sm">
        {!scoped && (
          <>
            <dt className="text-muted-foreground">{t("corridors_th_own")}</dt>
            <dd className="text-right">
              <OwnRatesCell
                coverage={coverage}
                organizationNames={organizationNames}
                onViewOrganization={actions.onViewOrganization}
              />
            </dd>
          </>
        )}
        <dt className="text-muted-foreground">{t("corridors_th_client")}</dt>
        <dd className="text-right">
          <TotalCell value={coverage.clientTotal} coverage={coverage} />
        </dd>
        <dt className="text-muted-foreground">{t("corridors_th_coloca")}</dt>
        <dd className="text-right">
          <TotalCell value={coverage.colocaTotal} coverage={coverage} />
        </dd>
      </dl>

      <div className="flex flex-wrap items-center gap-2 border-t border-border pt-3">
        {canWrite && (
          <ColocaButton variant="secondary" size="sm" icon={Pencil} onClick={() => actions.onEdit(coverage)}>
            {t(primaryLabelKey(coverage, scoped))}
          </ColocaButton>
        )}
        <ColocaButton variant="ghost" size="sm" icon={History} onClick={() => actions.onHistory(coverage)}>
          {t("action_history")}
        </ColocaButton>
        <span className="ml-auto">
          <ColocaDropdownMenu
            align="end"
            items={menu}
            trigger={
              <ColocaIconButton
                icon={MoreHorizontal}
                variant="ghost"
                size="sm"
                aria-label={t("action_more_aria", {
                  corridor: corridorLabel(coverage.corridor.route, labels.directionShort),
                })}
              />
            }
          />
        </span>
      </div>
    </article>
  );
}
